import { getProjects, getArchivedProjects, deleteProject as deleteProjectApi } from '../services/api';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';
import { PMProject } from '../types/pm';
import { notifyDataChanged } from '../utils/dataEvents';

// 訊息用函式：切換語言後顯示新語言（模組載入時就 i18n.t() 會固定成當時的語言）
const messages = {
  deleteSuccess: () => i18n.t('common.messages.deleteSuccess'),
  deleteError: () => i18n.t('common.messages.deleteFailed'),
  fetchError: () => i18n.t('common.messages.fetchFailed'),
};

const useProjectStoreBase = createEntityStore<PMProject>({
  entityName: 'project',
  fetchFn: (page: number) => getProjects(page),
  deleteFn: deleteProjectApi,
  toast,
  messages,
});

// 已封存專案
const useArchivedProjectStoreBase = createEntityStore<PMProject>({
  entityName: 'archivedProject',
  fetchFn: (page: number) => getArchivedProjects(page),
  deleteFn: deleteProjectApi,
  toast,
  messages,
});

export { useProjectStoreBase, useArchivedProjectStoreBase };

type ProjectStoreBase = typeof useProjectStoreBase;

/** 列表頁用：刪除照 createEntityStore（樂觀移除、失敗回滾），完成後通知儀表板等訂閱者（照 CRM projectStore） */
const useProjectListStore = (base: ProjectStoreBase) => {
  const store = base();
  return {
    projects: store.items,
    pagination: store.pagination,
    error: store.error,
    isLoading: store.isLoading,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    isDeleting: store.isDeleting,
    fetchProjects: store.fetchItems,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete: async () => {
      await store.confirmDelete();
      notifyDataChanged('projects');
    },
    closeDeleteModal: store.closeDeleteModal,
    refreshData: store.refreshData,
  };
};

export const useProjectStore = () => useProjectListStore(useProjectStoreBase);
export const useArchivedProjectStore = () => useProjectListStore(useArchivedProjectStoreBase);

/** 新增或編輯後寫回列表（封存列表裡編輯的專案也要更新；id 不在該列表時 updateItem 不動作） */
export const updateProjectInLists = (id: string, data: Partial<PMProject>) => {
  useProjectStoreBase.getState().updateItem(id, data);
  useArchivedProjectStoreBase.getState().updateItem(id, data);
};
